import { Capacitor } from '@capacitor/core';
import { getApiOrigin } from './apiConfig';

/**
 * Virtual WebView hostname (not real DNS). Keep in sync with `server.hostname`
 * in capacitor.config.ts. Avoids the literal "localhost" string; Zitadel must
 * list `capacitor://<host>` and `http://<host>` under Additional origins.
 */
export const CAPACITOR_APP_HOSTNAME = 'cg.app';
const WEB_PRODUCTION_HOST_ALIASES = new Set(['app.connectgroove.com', 'craft2.manucraft3d.com', 'leads.trevi.ca']);

const isBadNativeHttpRedirect = (value: string): boolean => {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    if (parsed.protocol !== 'https:') return true;
    return host === 'localhost' || host.endsWith('.localhost') || host === CAPACITOR_APP_HOSTNAME;
  } catch {
    return true;
  }
};
/**
 * Resolve env var with native overrides:
 * 1) `<BASE>_IOS` / `<BASE>_ANDROID`
 * 2) `<BASE>_NATIVE`
 */
export function getNativeOverridableEnvValue(envBase: string): string {
  if (!Capacitor.isNativePlatform()) return '';
  const platform = Capacitor.getPlatform();
  const platformSuffix = platform === 'ios' ? '_IOS' : platform === 'android' ? '_ANDROID' : '';
  const perPlatform = platformSuffix
    ? (import.meta.env[`${envBase}${platformSuffix}`] as string | undefined)?.trim()
    : '';
  if (perPlatform) return perPlatform;
  return (import.meta.env[`${envBase}_NATIVE`] as string | undefined)?.trim() || '';
}

function internalCapacitorAuthUrl(pathWithQueryAndHash: string): string {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') {
    return `https://${CAPACITOR_APP_HOSTNAME}${pathWithQueryAndHash}`;
  }
  return `capacitor://${CAPACITOR_APP_HOSTNAME}${pathWithQueryAndHash}`;
}

function isAlreadyOnCapacitorAppOrigin(): boolean {
  const { protocol, hostname } = window.location;
  if (protocol === 'capacitor:') return true;
  if (hostname === CAPACITOR_APP_HOSTNAME) return true;
  return false;
}

function oauthResponseInUrl(): boolean {
  const { search, hash } = window.location;
  return (
    /(?:^|[?&#])(?:code|error)=/.test(search) ||
    /(?:^|[?&#])(?:code|error)=/.test(hash)
  );
}

function nativeOAuthHandoffPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function currentWebProductionAliasOrigin(): string {
  if (Capacitor.isNativePlatform()) return '';
  if (window.location.protocol !== 'https:') return '';
  if (!WEB_PRODUCTION_HOST_ALIASES.has(window.location.hostname)) return '';
  return window.location.origin;
}

function envOriginIsProductionAlias(value: string): boolean {
  try {
    return WEB_PRODUCTION_HOST_ALIASES.has(new URL(value).hostname);
  } catch {
    return false;
  }
}

function shouldUpgradeAndroidInAppHttpToHttps(): boolean {
  // Do not rely on Capacitor native detection here: when an old callback bridge
  // forces `http://cg.app/...`, runtime detection can degrade to "web" before our
  // React/auth bootstrap, yet we still must normalize to secure origin.
  //
  // `cg.app` is an internal virtual host used by the native WebView in this app;
  // upgrading `http` -> `https` is safe and prevents PKCE/storage partition drift.
  return window.location.protocol === 'http:' && window.location.hostname === CAPACITOR_APP_HOSTNAME;
}

/**
 * Defensive normalization for Android:
 * older deployed callback bridges may still bounce to `http://cg.app/...`.
 * Force-upgrade to `https://cg.app/...` before React/OIDC bootstraps.
 */
export function redirectAndroidInAppHttpToHttpsIfNeeded(): boolean {
  if (!shouldUpgradeAndroidInAppHttpToHttps()) return false;
  const target = `https://${CAPACITOR_APP_HOSTNAME}${nativeOAuthHandoffPath()}`;
  window.location.replace(target);
  return true;
}

/**
 * True when we are on the public HTTPS document right after IdP redirect and
 * must `location.replace` to the Capacitor origin before React boots (so
 * `UserManager` / PKCE state never touch the wrong localStorage partition).
 */
export function shouldDeferReactBootstrapForNativeOAuthHandoff(): boolean {
  if (shouldUpgradeAndroidInAppHttpToHttps()) return true;
  if (!Capacitor.isNativePlatform()) return false;
  if (isAlreadyOnCapacitorAppOrigin()) return false;
  if (!oauthResponseInUrl()) return false;
  const path = nativeOAuthHandoffPath();
  return path.includes('/auth/callback') || path.includes('/auth/logout');
}

/**
 * Zitadel returns to the public HTTPS redirect URI. PKCE/session state for
 * oidc-client-ts lives on the Capacitor origin, so we immediately hand off the
 * same path, query, and hash to the internal WebView URL.
 *
 * Call synchronously at the start of `/auth/callback` (returns true if it navigated away).
 */
export function redirectCapacitorOAuthLoginBridgeIfNeeded(): boolean {
  if (!Capacitor.isNativePlatform()) return false;
  if (isAlreadyOnCapacitorAppOrigin()) return false;
  if (!oauthResponseInUrl()) return false;

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (!path.includes('/auth/callback')) return false;

  const target = internalCapacitorAuthUrl(path);
  window.location.replace(target);
  return true;
}

/**
 * Same idea as login: end-session may redirect to HTTPS logout URL; finish on
 * Capacitor origin so the SPA logout handler runs with consistent storage.
 */
export function redirectCapacitorOAuthLogoutBridgeIfNeeded(): boolean {
  if (!Capacitor.isNativePlatform()) return false;
  if (isAlreadyOnCapacitorAppOrigin()) return false;

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (!path.includes('/auth/logout')) return false;

  const target = internalCapacitorAuthUrl(path);
  window.location.replace(target);
  return true;
}

/**
 * OIDC redirect_uri.
 *
 * Zitadel **Web** applications reject custom schemes (`capacitor://`, etc.).
 * So native uses the **same HTTPS** `VITE_ZITADEL_*` URIs as the browser; the
 * IdP returns to https://…/auth/callback, then {@link redirectCapacitorOAuthLoginBridgeIfNeeded}
 * immediately sends the WebView to `capacitor://cg.app/...` with the same query/hash
 * so PKCE state in localStorage (Capacitor origin) still matches.
 *
 * Optional: set `VITE_ZITADEL_REDIRECT_URI_NATIVE` only if your Zitadel app type
 * allows native schemes and you want to skip the HTTPS hop.
 */
export function getZitadelRedirectUri(): string {
  const nativeExplicit = getNativeOverridableEnvValue('VITE_ZITADEL_REDIRECT_URI');
  if (Capacitor.isNativePlatform() && nativeExplicit && !isBadNativeHttpRedirect(nativeExplicit)) {
    return nativeExplicit;
  }

  const fromEnv = (import.meta.env.VITE_ZITADEL_REDIRECT_URI as string | undefined)?.trim();
  const currentAliasOrigin = currentWebProductionAliasOrigin();
  if (currentAliasOrigin && (!fromEnv || envOriginIsProductionAlias(fromEnv))) {
    return `${currentAliasOrigin}/auth/callback`;
  }
  if (fromEnv && !(Capacitor.isNativePlatform() && isBadNativeHttpRedirect(fromEnv))) return fromEnv;

  if (Capacitor.isNativePlatform()) {
    // Prefer a public HTTPS origin (tunnel/prod domain) so the IdP callback can load.
    // Then oauthHandoff.ts bridges back into the app WebView origin.
    const nativeApiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
    const nativeOrigin = (nativeApiBase || getApiOrigin()).replace(/\/api\/?$/i, '').replace(/\/+$/, '');
    if (/^https:\/\//i.test(nativeOrigin)) {
      return `${nativeOrigin}/auth/callback`;
    }
    if (Capacitor.getPlatform() === 'android') {
      return `https://${CAPACITOR_APP_HOSTNAME}/auth/callback`;
    }

    return `capacitor://${CAPACITOR_APP_HOSTNAME}/auth/callback`;
  }

  return '';
}

export function getZitadelPostLogoutRedirectUri(): string {
  const nativeExplicit = getNativeOverridableEnvValue('VITE_ZITADEL_POST_LOGOUT_REDIRECT_URI');
  if (Capacitor.isNativePlatform() && nativeExplicit && !isBadNativeHttpRedirect(nativeExplicit)) {
    return nativeExplicit;
  }

  const envLogout = (import.meta.env.VITE_ZITADEL_POST_LOGOUT_REDIRECT_URI as string | undefined)?.trim();
  const currentAliasOrigin = currentWebProductionAliasOrigin();
  if (currentAliasOrigin && (!envLogout || envOriginIsProductionAlias(envLogout))) {
    return `${currentAliasOrigin}/auth/logout`;
  }
  if (envLogout && !(Capacitor.isNativePlatform() && isBadNativeHttpRedirect(envLogout))) return envLogout;

  if (Capacitor.isNativePlatform()) {
    // Prefer a public HTTPS origin (tunnel/prod domain) so logout callback can load.
    const nativeApiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
    const nativeOrigin = (nativeApiBase || getApiOrigin()).replace(/\/api\/?$/i, '').replace(/\/+$/, '');
    if (/^https:\/\//i.test(nativeOrigin)) {
      return `${nativeOrigin}/auth/logout`;
    }
    if (Capacitor.getPlatform() === 'android') {
      return `https://${CAPACITOR_APP_HOSTNAME}/auth/logout`;
    }

    return `capacitor://${CAPACITOR_APP_HOSTNAME}/auth/logout`;
  }

  return `${window.location.origin}/auth/logout`;
}
